import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SchemaForm from 'components/common/SchemaForm';
import TestAction from 'components/common/TestAction';
import { getButtonText } from 'components/common/TestActionFunctions';
import {
  SCHEDULE_TOGGLE_FIELD,
  ConfigTarget,
  actionDescription,
  actionIsSwitchedOn,
  configFor,
  hasScheduleToggle,
  isBlank,
  missingRequiredFields,
} from './actionSetup';
import ScheduleToggle from './ScheduleToggle';
import { DraftReferenceState } from 'api/referenceOptions';

export type TestActionProps = {
  // The third argument names the integration the action belongs to. Screens that
  // show more than one account (the flow map) must pass it — without it the test
  // runs against whatever account the parent defaults to.
  // `formData` carries the section's unsaved edits, so a test runs against the
  // credentials on screen.
  onTestAction: (
    actionId: any,
    actionValue: any,
    target?: ConfigTarget,
    formData?: any,
  ) => void;
  loading?: boolean;
  response?: any;
};

type ActionSectionFormProps = {
  target: ConfigTarget;
  action: any;
  formData: any;
  formVersion?: number;
  errors?: Record<string, string>;
  editable?: boolean;
  userIsAdmin?: boolean;
  token?: string;
  onChange: (data: any, fieldId?: string) => void;
  test?: TestActionProps;
  // Create flows have nothing saved yet by definition, so the never-saved
  // banner and the required-field hints would state the obvious on every field.
  showSetupState?: boolean;
  // Per-field "Required — not configured yet." hints only. Off for sections whose
  // fields aren't in use right now (a dormant or exempt flow direction): the
  // hints read as errors the user owes, contradicting the notice above the form.
  // The never-saved banner stays — it is true whatever the direction's state.
  showRequiredHints?: boolean;
  // Replaces the Test Action row — create flows show it disabled with a
  // "save first" tooltip instead of a live button.
  headerActions?: React.ReactNode;
  // Off when the host puts the section's on/off switch in its own header (the
  // creation screen stacks sections under a heading and wants the switch on
  // that line). The switch's EFFECTS — read-only fields, no required hints —
  // apply either way; this only says who draws it.
  renderScheduleToggle?: boolean;
  // Create flows pass this so `gundi:reference` fields can fetch live options
  // against the credentials the user just typed, before the integration is saved.
  draftReference?: DraftReferenceState;
  // Read at click time. useConfigEditor stashes edits in a ref and re-renders
  // only on the first one, so the `formData` prop trails the form.
  getFormData?: () => any;
  // False when the screen shows the section description in the heading
  // instead (the stacked create screens, beside the on/off switch).
  showDescription?: boolean;
};

// One action's configuration form: the section body every configuration screen
// renders. Owns the two "this isn't live yet" signals — the never-saved banner and
// the per-field required hints — so they read identically everywhere.
const ActionSectionForm = ({
  target,
  action,
  formData,
  formVersion = 0,
  errors,
  editable = true,
  userIsAdmin = false,
  token,
  onChange,
  test,
  showSetupState = true,
  showRequiredHints = true,
  headerActions,
  renderScheduleToggle = true,
  draftReference,
  getFormData,
  showDescription = true,
}: ActionSectionFormProps) => {
  const { t } = useTranslation();
  const [tested, setTested] = useState(false);
  const saved = configFor(target, action);
  // A switched-off action is never run by the connector, so its fields are
  // read-only and nothing about them is owed: no required hints, and the
  // screens skip validating it (see actionIsSwitchedOn).
  const switchedOn = actionIsSwitchedOn(action, formData);
  const fieldsEditable = editable && switchedOn;

  // Sections swap this component's props rather than remounting it, so the
  // "a test ran" flag has to follow the action or the previous action's response
  // shows up under the next one. The account counts as much as the action: two
  // accounts of the same technology share action ids, so one account's result
  // would otherwise be displayed under the other's.
  useEffect(() => {
    setTested(false);
  }, [action.id, target.id]);

  // Mark required fields the saved configuration has no value for, the same way
  // validation errors render — that's the "what's missing" pointer the "!" badge
  // promises. A field showing a schema default gets a different sentence: the
  // input isn't empty, so "required" would read as a contradiction, but the value
  // on screen is not the one Gundi holds.
  const properties = action.schema?.properties ?? {};
  const missing =
    showSetupState && showRequiredHints && switchedOn
      ? missingRequiredFields(target, action)
      : [];
  const hints: Record<string, string> = {};
  missing.forEach((f) => {
    if (isBlank(properties[f]?.default))
      hints[f] = t('Required — not configured yet.');
    // Only worth saying once the rest of the action is saved. With nothing saved
    // at all the banner above already says every value on screen is an unsaved
    // default, and repeating that per field adds nothing.
    else if (saved)
      hints[f] = t('Showing the default value — save to apply it.');
  });
  const setupHints = Object.keys(hints).length > 0 ? hints : undefined;
  const togglesHere = renderScheduleToggle && hasScheduleToggle(action);
  // Drawn beside the switch when this form owns it; otherwise the form keeps it.
  const descriptionInHeader =
    showDescription && togglesHere ? actionDescription(action) : '';

  return (
    <div className="rounded border border-ui-field-outline bg-white p-4">
      {showSetupState && !saved && (
        <div className="mb-3 rounded border border-unlisted-orange-200 bg-unlisted-orange-50 px-4 py-3 text-sm text-ui-text-primary-text">
          {t(
            "This action hasn't been saved yet. The values below are defaults and won't apply until you save.",
          )}
        </div>
      )}
      {/* Same corner as the live Test Route button, so a create form and
                an existing account's form have the same layout. Both header
                variants draw their own row through TestActionLayout. */}
      {(headerActions || togglesHere) && (
        // A line's worth more space than mb-3, so the note and switch read
        // as the section's header rather than the first field.
        <div className="mb-8 flex items-center justify-end gap-3">
          {(headerActions || descriptionInHeader) && (
            <div className="min-w-0 flex-1">
              {/* The section's note sits left of its switch, the same
                                place the create screens put it (their heading row). */}
              {descriptionInHeader && (
                <p className="text-sm italic text-ui-text-primary-text">
                  {descriptionInHeader}
                </p>
              )}
              {headerActions}
            </div>
          )}
          {renderScheduleToggle && (
            <ScheduleToggle
              action={action}
              data={formData}
              onChange={onChange}
              disabled={!editable}
            />
          )}
        </div>
      )}
      {action.schema?.is_executable === true && test && (
        <div className="mb-3">
          <TestAction
            actionId={action.id}
            actionValue={action.value}
            onTestAction={(id: any, value: any) => {
              test.onTestAction(
                id,
                value,
                target,
                getFormData ? getFormData() : formData,
              );
              setTested(true);
            }}
            isLoading={!!test.loading && tested}
            response={tested ? test.response : null}
            buttonText={getButtonText(action.value, t)}
            formTitle=""
            uiSchema={action.ui_schema || {}}
          />
        </div>
      )}
      <SchemaForm
        key={`${action.id}-v${formVersion}`}
        // Unique per action AND per account: the flow map shows two
        // accounts of the same technology, which share action ids.
        idPrefix={`${target.id ? `${target.id}-` : ''}${action.id}`}
        userIsAdmin={userIsAdmin}
        formTitle=""
        schema={action.schema}
        // The switch above owns run_on_schedule, so the checkbox rjsf would
        // render for it is hidden rather than dropped — omitting a property
        // that `ui:order` lists makes the form fail silently.
        ui_schema={{
          ...(action.ui_schema
            ? {
                ...action.ui_schema,
                'ui:description': action.ui_schema['ui:description'] || '',
              }
            : {}),
          ...(hasScheduleToggle(action)
            ? {
                [SCHEDULE_TOGGLE_FIELD]: {
                  ...(action.ui_schema?.[SCHEDULE_TOGGLE_FIELD] || {}),
                  'ui:widget': 'hidden',
                },
              }
            : {}),
        }}
        formdata={formData}
        onChange={(e: any, fieldId?: string) => onChange(e, fieldId)}
        isEditable={fieldsEditable}
        errors={errors ?? setupHints}
        integrationId={target.id}
        authToken={token}
        draftReference={draftReference}
        hideRootDescription={!showDescription || !!descriptionInHeader}
      />
    </div>
  );
};

export default ActionSectionForm;
